import type { FeatureFlagValue, FeatureFlagValueType } from "./types";
import { FeatureFlagValueType as VT } from "./types";

const MAX_INLINE_STRING_LENGTH = 60;

/**
 * Short human-readable representation of a flag value used in the list /
 * row view. Never throws — falls back to JSON.stringify. The full (and
 * formatted) value is shown on the detail page.
 */
export function formatValueInline(
  value: FeatureFlagValue,
  valueType: FeatureFlagValueType,
): string {
  if (value === null || value === undefined) return "null";

  switch (valueType) {
    case VT.BOOLEAN:
      return value === true ? "true" : value === false ? "false" : String(value);
    case VT.NUMBER:
      return typeof value === "number" ? String(value) : JSON.stringify(value);
    case VT.STRING: {
      const str = typeof value === "string" ? value : JSON.stringify(value);
      if (str.length <= MAX_INLINE_STRING_LENGTH) return `"${str}"`;
      return `"${str.slice(0, MAX_INLINE_STRING_LENGTH - 1)}…"`;
    }
    case VT.JSON: {
      try {
        const serialized = JSON.stringify(value);
        if (serialized.length <= MAX_INLINE_STRING_LENGTH) return serialized;
        return `${serialized.slice(0, MAX_INLINE_STRING_LENGTH - 1)}…`;
      } catch {
        return "[unserializable]";
      }
    }
    default:
      return JSON.stringify(value);
  }
}

/**
 * Pretty-print a flag value for the detail view / history entries.
 * Multi-line JSON gets indentation; primitives are rendered tightly.
 */
export function formatValueBlock(value: FeatureFlagValue, valueType: FeatureFlagValueType): string {
  if (value === null || value === undefined) return "null";

  if (valueType === VT.STRING && typeof value === "string") {
    return value;
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}
